package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.warning
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.Query
import com.siriusxm.pia.views.unifiedaggregator.RouteLocation
import com.siriusxm.pia.views.unifiedaggregator.events.encodeTimeRange
import com.siriusxm.pia.views.unifiedaggregator.events.parseTimeRange
import com.siriusxm.pia.views.unifiedaggregator.events.partialEventView
import unifiedcontent.aggregator.AuditContext
import unifiedcontent.aggregator.ContentAuditEntry
import unifiedcontent.aggregator.ContentUpdateType
import unifiedcontent.aggregator.PartialUpdate
import unifiedcontent.aggregator.PartialUpdateOperation
import io.ktor.client.request.*
import io.ktor.client.statement.*
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException
import kotlin.time.Instant
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.jsonObject
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

/**
 * Global content audit log page for the aggregator. Displays the most recent content entity
 * changes across all producers in a paginated, expandable table. Each row shows when the
 * change was made, who made it, which entity was affected, and the type of operation.
 * Expanding a row reveals field-level change details: for partial updates the modification
 * data is rendered using the Smithy model (same as the Incoming tab); for full entity updates
 * the sets of added/modified/removed fields are shown.
 *
 * Uses GET /audit/content via the aggregator SDK's getContentAudit method. The Download CSV
 * item in the header's menu uses GET /audit/content/export for the same time range (UCA-676),
 * and the header's left side shows "Preparing CSV" while the export is built.
 *
 * The selected time range lives in the URL (`#aggregator/audit?range=1w`), so the view can be
 * deep-linked and browser Back steps through range changes. With no range in the URL the page
 * shows the last week, which keeps the first load small.
 *
 * @param aggregator The aggregator service instance used for data fetching.
 * @param query The query string of the current URL, carrying the `range` parameter.
 */
@Composable
fun ServiceViewContext.ContentAuditLog(aggregator: AggregatorService, query: Query = Query.Empty) {
    var entries by remember { mutableStateOf<List<ContentAuditEntry>?>(null) }
    val timeRange = query[AUDIT_RANGE_PARAM]?.let { parseTimeRange(it) } ?: DEFAULT_AUDIT_TIME_RANGE
    var preparingCsv by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    serviceContentView({
        title = "Audit Log"
        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Audit Log", null)
            }
        }
    }) {
        box({
            paddedContent = false
            header({
                content {
                    // The header's left side shows export progress; it is otherwise empty.
                    if (preparingCsv) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                                fontSize(13.px)
                                color(SXMUI.subtleTextColor.value())
                            }
                        }) {
                            spinner(size = Size.SMALL)
                            Text("Preparing CSV")
                        }
                    }
                }
                actionContent {
                    timeRangeSelect(selected = timeRange) { selected ->
                        // Writing the range is a navigation, so the URL is the single source of
                        // truth. Clearing a custom range falls back to the default (no parameter).
                        val range = selected?.takeIf { it != DEFAULT_AUDIT_TIME_RANGE }?.let { encodeTimeRange(it) }
                        aggregator.navigate(
                            RouteLocation(listOf("audit"), Query.Empty.with(AUDIT_RANGE_PARAM, range)).toPath()
                        )
                    }
                    LazyPaginationControl(
                        key = timeRange,
                        updater = { entries = it },
                        fetcher = { token ->
                            val range = timeRange.range()
                            val result = aggregator.clientApi.getContentAudit(
                                limit = 50,
                                start = range.start,
                                end = range.endInclusive,
                                nextToken = token
                            )
                            PageContents(result.entries, result.nextToken)
                        },
                        onError = { err ->
                            aggregator.notifications.error("Failed to load audit log: ${err.message}")
                        }
                    )
                    MenuButton {
                        align = PopupAlign.Right
                        addMenuItem {
                            title = "Download CSV"
                            action {
                                // Run the export in the page's scope so the menu closes at once while
                                // the header shows progress. Repeat clicks during an export are ignored.
                                if (!preparingCsv) {
                                    preparingCsv = true
                                    scope.launch {
                                        try {
                                            val range = timeRange.range()
                                            downloadContentAuditCsv(aggregator, range.start, range.endInclusive)
                                        } finally {
                                            preparingCsv = false
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            })
        }) {

            when {
                entries == null -> boxSpinner("Loading audit log...")
                entries!!.isEmpty() -> Div({
                    style {
                        textAlign("center")
                        padding(40.px)
                        color(SXMUI.subtleTextColor.value())
                    }
                }) {
                    Text("No audit entries found.")
                }
                else -> {
                    table<ContentAuditEntry> {
                        items(entries!!)

                        column {
                            width = 160.px
                            content { entry ->
                                Div({
                                    style {
                                        fontSize(13.px)
                                        marginBottom(2.px)
                                    }
                                }) {
                                    // Render in the globally-selected timezone (header picker),
                                    // not the browser's system zone.
                                    Text(entry.timestamp.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString())
                                }
                                Div({
                                    style {
                                        fontSize(12.px)
                                        color(SXMUI.subtleTextColor.value())
                                    }
                                }) {
                                    Text(entry.author.substringBefore("@"))
                                }
                            }
                        }

                        column {
                            content { entry ->
                                // Primary: entityName if available, else entityId — linked to entity page
                                A(href = "#aggregator/entity/${encodeURIComponent(entry.entityId)}", {
                                    style { fontSize(13.px) }
                                }) {
                                    Text(entry.entityName?.takeIf { it.isNotBlank() } ?: entry.entityId)
                                }
                                // Secondary: entity type name
                                Div({
                                    style {
                                        fontSize(12.px)
                                        color(SXMUI.subtleTextColor.value())
                                    }
                                }) {
                                    val typeName = aggregator.entityType(entry.entityType)
                                        ?.name?.takeIf { it.isNotBlank() }
                                        ?: entry.entityType
                                    Text(typeName)
                                }
                                // Tertiary: human-readable change description
                                entry.description?.let { desc ->
                                    Div({
                                        style {
                                            fontSize(11.px)
                                            color(SXMUI.subtleTextColor.value())
                                            marginTop(2.px)
                                        }
                                    }) {
                                        Text(desc)
                                    }
                                }
                            }
                        }


                        expandable { entry ->
                            ContentAuditEntryDetail(aggregator, entry)
                        }
                    }
                }
            }
        }
    }
}

/** The query parameter carrying the selected time range, in the same format as the events views. */
private const val AUDIT_RANGE_PARAM = "range"

/** The range shown when the URL has none: the last week. */
private val DEFAULT_AUDIT_TIME_RANGE = TimeRange(relative = PeriodType.Weeks.periodOf(1))

/** Upper bound on rows the console will stitch into one download before asking for a narrower range. */
private const val CSV_DOWNLOAD_ROW_LIMIT = 50_000

/**
 * Download the content audit CSV export for [start]..[end] as a single file.
 *
 * The export API returns at most 10,000 rows per response and signals the rest with an
 * `X-Export-Next-Token` header. This follows those tokens, keeps the header row from the first
 * chunk only, and stops at [CSV_DOWNLOAD_ROW_LIMIT] rows. It calls the endpoint through the SDK's
 * authenticated HttpClient because the generated SDK method does not surface response headers.
 */
private suspend fun downloadContentAuditCsv(aggregator: AggregatorService, start: Instant, end: Instant) {
    val api = aggregator.clientApi
    val body = StringBuilder()
    var fileName = "content-audit.csv"
    var rows = 0
    var token: String? = null
    var truncated: Boolean

    try {
        do {
            val response = api.client.get("${api.baseUrl.trimEnd('/')}/audit/content/export") {
                parameter("start", start.toString())
                parameter("end", end.toString())
                parameter("maxRows", minOf(10_000, CSV_DOWNLOAD_ROW_LIMIT - rows))
                token?.let { parameter("nextToken", it) }
            }
            val chunk = response.bodyAsText()
            if (body.isEmpty()) {
                body.append(chunk)
                response.headers["Content-Disposition"]
                    ?.substringAfter("filename=", "")
                    ?.trim('"')
                    ?.takeIf { it.isNotBlank() }
                    ?.let { fileName = it }
            } else {
                // Every chunk repeats the header row; keep only the first one.
                body.append(chunk.substringAfter("\r\n"))
            }
            rows += response.headers["X-Export-Row-Count"]?.toIntOrNull() ?: 0
            truncated = response.headers["X-Export-Truncated"] == "true"
            token = response.headers["X-Export-Next-Token"]
        } while (truncated && token != null && rows < CSV_DOWNLOAD_ROW_LIMIT)
    } catch (e: CancellationException) {
        // The page was left mid-download; nothing failed.
        throw e
    } catch (e: Exception) {
        aggregator.notifications.error("Failed to export audit log: ${e.message}")
        return
    }

    saveFile(fileName, body.toString(), "text/csv;charset=utf-8")

    if (truncated) {
        aggregator.notifications.warning(
            "Export stopped at $rows rows",
            "More entries match this time range. Choose a narrower range to export the rest."
        )
    }
}

/** Hand [content] to the browser as a file download. */
private fun saveFile(fileName: String, content: String, mimeType: String) {
    val blob = Blob(arrayOf(content), BlobPropertyBag(type = mimeType))
    val url = URL.createObjectURL(blob)
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = url
    anchor.download = fileName
    document.body?.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
}

/**
 * Renders the expanded detail section for a single content audit entry.
 *
 * For partial updates, uses the entity modification snapshot and operations list to render
 * each changed field using the Smithy model — the same approach used by the Incoming tab's
 * partial event view. For full entity updates, shows the lists of added/modified/removed
 * field names from the FieldChanges snapshot. Falls back to a plain text message when no
 * structured change data is available.
 *
 * @param aggregator The aggregator service for Smithy model and entity type lookups.
 * @param entry The content audit entry whose details are being displayed.
 */
@Composable
private fun ContentAuditEntryDetail(aggregator: AggregatorService, entry: ContentAuditEntry) {
    Div({
        style {
            padding(10.px, 20.px, 10.px, 0.px)
        }
    }) {
        entry.source?.let { src ->
            Div({
                style {
                    fontSize(12.px)
                    color(SXMUI.subtleTextColor.value())
                    marginBottom(8.px)
                }
            }) {
                Text("Source: $src")
            }
        }

        when {
            entry.partialId != null && entry.entity != null -> {
                val operations = runCatching {
                    Json.decodeFromJsonElement<List<PartialUpdateOperation>>(
                        entry.operations as? JsonArray ?: JsonArray(emptyList())
                    )
                }.getOrNull()

                val partial = PartialUpdate(
                    type = entry.operation,
                    timestamp = entry.timestamp,
                    modification = entry.entity!!,
                    partialId = entry.partialId!!,
                    operations = operations,
                    auditContext = AuditContext(author = entry.author)
                )
                partialEventView(aggregator, partial)
            }
            entry.partialId != null -> {
                AuditSubtleText("Partial update applied — no detail available.")
            }
            entry.changes != null -> {
                val fieldChanges = entry.changes!!
                val added = fieldChanges.fieldsAdded.orEmpty()
                val modified = fieldChanges.fieldsModified.orEmpty()
                val removed = fieldChanges.fieldsRemoved.orEmpty()

                if (added.isEmpty() && modified.isEmpty() && removed.isEmpty()) {
                    AuditSubtleText("No field-level changes recorded.")
                } else {
                    if (added.isNotEmpty()) ChangeFieldGroup("Added", added, BadgeColor.GREEN)
                    if (modified.isNotEmpty()) ChangeFieldGroup("Modified", modified, BadgeColor.BLUE)
                    if (removed.isNotEmpty()) ChangeFieldGroup("Removed", removed, BadgeColor.RED)
                }
            }
            entry.operation == ContentUpdateType.ADD -> {
                AuditSubtleText("Entity was created.")
            }
            else -> {
                AuditSubtleText("No detail available.")
            }
        }
    }
}

/**
 * Renders a labeled group of field names with a colored badge, used to display
 * the set of fields added, modified, or removed in a full entity update.
 */
@Composable
private fun ChangeFieldGroup(label: String, fields: List<String>, color: BadgeColor) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            alignItems(AlignItems.FlexStart)
            gap(8.px)
            marginBottom(6.px)
        }
    }) {
        Badge(label, color)
        Div({
            style {
                fontSize(12.px)
                lineHeight(20.px)
            }
        }) {
            Text(fields.joinToString(", "))
        }
    }
}

/**
 * Renders a muted inline text message for fallback detail states in audit entry rows.
 */
@Composable
private fun AuditSubtleText(message: String) {
    Div({
        style {
            fontSize(12.px)
            color(SXMUI.subtleTextColor.value())
        }
    }) {
        Text(message)
    }
}

